/* eslint-disable tsdoc/syntax */
import type { ComponentType } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
//* Layouts
import {
  MainLayout,
  AuthenticationLayout,
  SignUpDetailsLayout,
  TiersLayout,
  SettingsLayout,
  PaymentLinkLayout,
} from "@zenith/layouts";

//* Pages
import {
  AccountCreatedPage,
  UpgradeAccountTierPage,
  CreateNewPinPage,
  AcceptInvitationPage,
  SelectProfilePage,
  DashboardPage,
  ForgotPinPage,
  LandingPage,
  MobileVerificationPage,
  PaymentsPage,
  PendingSalePage,
  ConfirmTransferPage,
  CollectPaymentPage,
  MakePaymentPage,
  PaymentOverviewPage,
  PaymentSuccessfulPage,
  PinCreatedPage,
  SignInPage,
  SignUpPage,
  TransactionsPage,
  USSDCodePage,
  USSDCodePendingPage,
  BankTransferPage,
  BankTransferPendingPage,
  SendMoneyPage,
  SendMoneyOverviewPage,
  SendMoneyTransactionPinPage,
  SendMoneySuccessfulPage,
  RefundPage,
  RefundOverviewPage,
  RefundSuccessfulPage,
  RefundTransactionPinPage,
  BeneficiariesPage,
  VirtualAccountsPage,
  CreateVirtualAccountPage,
  VirtualAccountDetailsPage,
  OutletManagementPage,
  CreateOutletPage,
  PosTransactionHistoryPage,
  WithdrawalHistoryPage,
  TransactionDetailsPage,
  QRCodePage,
  QRCodePendingPage,
  PosManagementPage,
  PosDetailsPage,
  PosViewOfferPage,
  PosRequestReviewPage,
  PosTransactionPinPage,
  PosRequestSubmitPage,
  PosPaymentMethodPage,
  // LoansPage,
  AnalyticsPage,
  UndoSalePinPage,
  SettingsPage,
  PaymentLinkPublicPage,
  PublicReceiptPage,
  PaymentLinkOverviewPage,
  PaymentLinkPage,
  ProfileManagementPage,
  LoansSuccessfulPage,
  TransactionPinPage,
  ChangePasswordPage,
  PaymentLinkMethodPage,
  PaymentLinkBankTrfDetails,
  PaymentLinkUssdCodeDetails,
  TeamManagementPage,
  TeamMemberDetailsPage,
  SystemPreferencePage,
  NotificationPreferencesPage,
  PaymentLinkBankTrfPending,
  PaymentLinkSuccessPage,
  PaymentLinkUssdCodePending,
  EndUpgradePage,
  QRCodePaymentLinkDetailsPage,
  QRCodePaymentLinkPendingPage,
  RefundAuthenticationCodePage,
  SendMoneyAuthenticationCodePage,
  TwoFactorAuthenticationPage,
  PosRequestAuthenticationCodePage,
  StatementsPage,
  InventoryPage,
  AddNewProductPage,
  PaymentLinkCardPendingPage,
  OpenPaymentLinkAmountPage,
  OpenPaymentLinkMethodPage,
  OpenPaymentLinkBankTransferPage,
  OpenPaymentLinkQrCodePage,
  OpenPaymentLinkUssdPage,
  OpenPaymentLinkCardPendingPage,
  OpenPaymentLinkSuccessPage,
  SignTwoFaLayout,
  SignUpMobileVerificationPage,
  BusinessRegistrationPage,
  ProfileCreationPage,
  BusinessRegistrationTypePage,
  BusinessVerificationPage,
  BusinessPersonalDetailsPage,
  BusinessPhotoVerificationPage,
  BusinessInformationPage,
  BusinessDocumentsPage,
  BusinessPeoplePage,
  BusinessPeopleVerifyPage,
  BusinessPeopleVerifyEmailLandingPage,
  BusinessPeopleVerifyEmailPage,
  BusinessAccountPage,
  BusinessReviewPage,
  BusinessSubmittingPage,
  BusinessUnderReviewPage,
  OperationConfirmationPage,
  PaymentLinkConfirmationPage,
} from "@zenith/routes/pages";
import { TokenHOC } from "@zenith/services/token";
import { ErrorHandlerPage } from "@zenith/pages/ErrorHandlerPage/error-handler-page/error-handler-page";
import { RouterChunkErrorBoundary } from "@zenith/routes/chunk-error-boundary";
import MerchantOnlyRoute from "./merchant-only-route";
import type { Permission } from "@zenith/features/AuthZ/permissions";
import {
  IS_MULTI_SIGNATORY_ENABLED,
  IS_OUTLET_MANAGEMENT_ENABLED,
  IS_REFUND_ENABLED,
} from "@zenith/data/constants";
//* Paths
import {
  ACCOUNT_CREATED,
  BENEFICIARIES,
  VIRTUAL_ACCOUNTS,
  VIRTUAL_ACCOUNT_CREATE,
  VIRTUAL_ACCOUNT_DETAILS,
  VIRTUAL_ACCOUNT_TRANSACTION_DETAILS,
  CREATE_NEW_PIN,
  ACCEPT_INVITATION,
  SELECT_PROFILE,
  DASHBOARD,
  FORGOT_PIN,
  // LANDING_PAGE,
  MOBILE_VERIFICATION,
  OUTLET_MANAGEMENT,
  OUTLET_CREATE,
  POS_TRANSACTION_HISTORY,
  OUTLET_WITHDRAWAL_HISTORY,
  PAYMENT,
  PAYMENT_BANK_TRANSFER,
  PAYMENT_BANK_TRANSFER_PENDING,
  PAYMENT_OVERVIEW,
  PAYMENT_SUCCESS,
  PAYMENT_USSD_CODE,
  PAYMENT_USSD_CODE_PENDING,
  PAYMENT_PENDING_SALE,
  PAYMENT_CONFIRM_TRANSFER,
  PIN_CREATED,
  SEND_MONEY,
  SEND_MONEY_OVERVIEW,
  SEND_MONEY_PIN,
  SEND_MONEY_SUCCESS,
  SIGN_IN,
  SIGN_UP,
  SIGN_UP_USER,
  SIGN_UP_PHOTO_VERIFICATION,
  TRANSACTIONS,
  TRANSACTIONS_TRANSACTION_DETAILS,
  TRANSACTIONS_REFUND,
  TRANSACTIONS_REFUND_OVERVIEW,
  TRANSACTIONS_REFUND_PIN,
  TRANSACTIONS_REFUND_SUCCESS,
  UPGRADE,
  UPGRADE_ACCOUNT,
  PAYMENT_QR_CODE,
  PAYMENT_QR_CODE_PENDING,
  POS_MANAGEMENT,
  POS_VIEW_OFFER,
  // LOANS,
  ANALYTICS,
  ANALYTICS_UNDO_SALE,
  SETTINGS,
  PAYMENT_LINK_PUBLIC_PAGE,
  PUBLIC_RECEIPT_PAGE,
  PAYMENT_LINK,
  PAYMENT_PAYMENT_LINK,
  POS_REQUEST_REVIEW,
  POS_DETAILS,
  POS_TRANSACTION_PIN,
  POS_REQUEST_SUBMIT,
  PROFILE_MANAGEMENT,
  TRANSACTION_PIN,
  CHANGE_PASSWORD,
  TWO_FACTOR_AUTH,
  TEAM_MANAGEMENT,
  TEAM_MEMBER_DETAILS,
  SYSTEM_PREFERENCE,
  NOTIFICATION_PREFERENCES,
  POS_PAYMENT_METHOD,
  LOANS_SUCCESS,
  PAYMENT_LINK_OVERVIEW_PAGE,
  PAYMENT_LINK_METHOD_PAGE,
  PAYMENT_LINK_BANK_TRF_DETAILS,
  PAYMENT_LINK_USSD_CODE_DETAILS,
  NEW_USER_PIN,
  END_UPGRADE_PAGE,
  PAYMENT_LINK_BANK_TRF_PENDING,
  PAYMENT_LINK_SUCCESS,
  PAYMENT_LINK_USSD_CODE_PENDING,
  PAYMENT_LINK_QR_CODE_DETAILS,
  PAYMENT_LINK_QR_CODE_PENDING,
  TRANSACTIONS_REFUND_AUTHENTICATION,
  SEND_MONEY_AUTHENTICATION,
  POS_AUTHENTICATION,
  STATEMENTS,
  INVENTORY,
  ADD_NEW_PRODUCT,
  COLLECT_PAYMENT,
  MAKE_PAYMENT,
  PAYMENT_LINK_CARD_PENDING,
  OPEN_PAYMENT_LINK_PAGE,
  OPEN_PAYMENT_LINK_METHOD,
  OPEN_PAYMENT_LINK_BANK_TRANSFER,
  OPEN_PAYMENT_LINK_QR_CODE,
  OPEN_PAYMENT_LINK_USSD,
  OPEN_PAYMENT_LINK_CARD_PENDING,
  OPEN_PAYMENT_LINK_SUCCESS,
  TWO_FACTOR_LAYOUT,
  ERROR_HANDLER,
  LANDING_PAGE,
  SIGN_UP_MOBILE_VERIFICATION,
  PROFILE_CREATION,
  BUSINESS_REGISTRATION,
  BUSINESS_REGISTRATION_TYPE,
  BUSINESS_VERIFICATION,
  BUSINESS_VERIFICATION_PERSONAL_DETAILS,
  BUSINESS_VERIFICATION_PHOTO_VERIFICATION,
  BUSINESS_VERIFICATION_BUSINESS_INFO,
  BUSINESS_VERIFICATION_DOCUMENTS,
  BUSINESS_VERIFICATION_PEOPLE,
  BUSINESS_VERIFICATION_PEOPLE_VERIFY,
  BUSINESS_VERIFICATION_PEOPLE_EMAIL_LANDING,
  BUSINESS_VERIFICATION_PEOPLE_INVITE_LINK,
  BUSINESS_VERIFICATION_PEOPLE_EMAIL_FORM,
  BUSINESS_VERIFICATION_ACCOUNT,
  BUSINESS_VERIFICATION_REVIEW,
  BUSINESS_VERIFICATION_SUBMITTING,
  BUSINESS_VERIFICATION_UNDER_REVIEW,
  OPERATION_CONFIRMATION,
  PAYMENT_LINK_CONFIRMATION,
} from "./paths";
import OnboardingLayout from "@zenith/layouts/OnboardingLayout/onboarding-layout";

/**
 * Screens BOAS-7641 scopes by role and business type - the permission names
 * which of the matrix's rows may open each one.
 */
const merchantOnly = (Component: ComponentType, permission: Permission) => (
  <MerchantOnlyRoute permission={permission}>
    <Component />
  </MerchantOnlyRoute>
);

/**
 * * Any page that needs the user to be authenticated
 * * needs to be created inside the TokenHOC component
 */
const appRoutes = createBrowserRouter([
  // * REGION Pages that don't require authentication
  {
    path: LANDING_PAGE,
    element: <Navigate to="/sign-in" replace />,
    errorElement: <ErrorHandlerPage />,
  },
  {
    Component: AuthenticationLayout,
    errorElement: <RouterChunkErrorBoundary />,
    children: [
      //#region Sign-In flow
      {
        path: SIGN_IN,
        Component: SignInPage,
      },
      {
        path: FORGOT_PIN,
        Component: ForgotPinPage,
      },
      {
        path: NEW_USER_PIN,
        element: <ForgotPinPage isNewUser />,
      },
      {
        path: MOBILE_VERIFICATION,
        Component: MobileVerificationPage,
      },
      {
        path: CREATE_NEW_PIN,
        Component: CreateNewPinPage,
      },
      {
        path: ACCEPT_INVITATION,
        Component: AcceptInvitationPage,
      },
      // The emailed link carries the token: /accept-invitation/{token}, and
      // /new-user for a business onboarded from the back office.
      {
        path: `${ACCEPT_INVITATION}/:token`,
        Component: AcceptInvitationPage,
      },
      {
        path: `${ACCEPT_INVITATION}/:token/new-user`,
        Component: AcceptInvitationPage,
      },
      {
        path: SELECT_PROFILE,
        Component: SelectProfilePage,
      },
      //#endregion Sign-In flow
      {
        path: SIGN_UP,
        Component: SignUpPage,
      },
      {
        path: TWO_FACTOR_LAYOUT,
        Component: SignTwoFaLayout,
      },
      {
        path: SIGN_UP_MOBILE_VERIFICATION,
        Component: SignUpMobileVerificationPage,
      },
    ],
  },

  {
    Component: OnboardingLayout,
    errorElement: <RouterChunkErrorBoundary />,
    children: [
      //#region Onboarding flow

      {
        path: PROFILE_CREATION,
        Component: ProfileCreationPage,
      },
      {
        path: BUSINESS_REGISTRATION,
        Component: BusinessRegistrationPage,
      },
      {
        path: BUSINESS_REGISTRATION_TYPE,
        Component: BusinessRegistrationTypePage,
      },
      // * Pilot only — the multi-signatory checklist is not released to
      // * production yet, so its entry route is registered only when the
      // * feature is switched on.
      ...(IS_MULTI_SIGNATORY_ENABLED
        ? [
            {
              path: BUSINESS_VERIFICATION,
              Component: BusinessVerificationPage,
            },
          ]
        : []),
    ],
  },
  // #region Business Verification (multi-signatory) Flow
  // * Pilot only — not released to production yet, so these routes are only
  // * registered when the feature is switched on. A direct or bookmarked hit
  // * goes back to the registration-type step.
  ...(IS_MULTI_SIGNATORY_ENABLED
    ? [
        {
          path: BUSINESS_VERIFICATION_PERSONAL_DETAILS,
          Component: BusinessPersonalDetailsPage,
          errorElement: <RouterChunkErrorBoundary />,
        },
        {
          path: BUSINESS_VERIFICATION_PHOTO_VERIFICATION,
          Component: BusinessPhotoVerificationPage,
          errorElement: <RouterChunkErrorBoundary />,
        },
        {
          path: BUSINESS_VERIFICATION_BUSINESS_INFO,
          Component: BusinessInformationPage,
          errorElement: <RouterChunkErrorBoundary />,
        },
        {
          path: BUSINESS_VERIFICATION_DOCUMENTS,
          Component: BusinessDocumentsPage,
          errorElement: <RouterChunkErrorBoundary />,
        },
        {
          path: BUSINESS_VERIFICATION_PEOPLE,
          Component: BusinessPeoplePage,
          errorElement: <RouterChunkErrorBoundary />,
        },
        {
          path: BUSINESS_VERIFICATION_PEOPLE_VERIFY,
          Component: BusinessPeopleVerifyPage,
          errorElement: <RouterChunkErrorBoundary />,
        },
        {
          path: BUSINESS_VERIFICATION_PEOPLE_EMAIL_LANDING,
          Component: BusinessPeopleVerifyEmailLandingPage,
          errorElement: <RouterChunkErrorBoundary />,
        },
        {
          path: BUSINESS_VERIFICATION_PEOPLE_INVITE_LINK,
          Component: BusinessPeopleVerifyEmailLandingPage,
          errorElement: <RouterChunkErrorBoundary />,
        },
        {
          path: BUSINESS_VERIFICATION_PEOPLE_EMAIL_FORM,
          Component: BusinessPeopleVerifyEmailPage,
          errorElement: <RouterChunkErrorBoundary />,
        },
        {
          path: BUSINESS_VERIFICATION_ACCOUNT,
          Component: BusinessAccountPage,
          errorElement: <RouterChunkErrorBoundary />,
        },
        {
          path: BUSINESS_VERIFICATION_REVIEW,
          Component: BusinessReviewPage,
          errorElement: <RouterChunkErrorBoundary />,
        },
        {
          path: BUSINESS_VERIFICATION_SUBMITTING,
          Component: BusinessSubmittingPage,
          errorElement: <RouterChunkErrorBoundary />,
        },
        {
          path: BUSINESS_VERIFICATION_UNDER_REVIEW,
          Component: BusinessUnderReviewPage,
          errorElement: <RouterChunkErrorBoundary />,
        },
      ]
    : [
        {
          path: BUSINESS_VERIFICATION,
          element: <Navigate to={PROFILE_CREATION} replace />,
        },
        {
          path: `${BUSINESS_VERIFICATION}/*`,
          element: <Navigate to={PROFILE_CREATION} replace />,
        },
      ]),
  // #endregion Business Verification (multi-signatory) Flow
  {
    path: PUBLIC_RECEIPT_PAGE,
    Component: PublicReceiptPage,
    errorElement: <RouterChunkErrorBoundary />,
  },
  //#region Payment Link flow
  {
    Component: PaymentLinkLayout,
    errorElement: <RouterChunkErrorBoundary />,
    children: [
      {
        path: PAYMENT_LINK,
        Component: PaymentLinkPublicPage,
      },
      {
        path: PAYMENT_LINK_PUBLIC_PAGE,
        Component: PaymentLinkPublicPage,
      },
      {
        path: PAYMENT_LINK_OVERVIEW_PAGE,
        Component: PaymentLinkOverviewPage,
      },
      {
        path: PAYMENT_LINK_METHOD_PAGE,
        Component: PaymentLinkMethodPage,
      },
      {
        path: PAYMENT_LINK_SUCCESS,
        Component: PaymentLinkSuccessPage,
      },
      {
        path: PAYMENT_LINK_BANK_TRF_DETAILS,
        Component: PaymentLinkBankTrfDetails,
      },
      {
        path: PAYMENT_LINK_BANK_TRF_PENDING,
        Component: PaymentLinkBankTrfPending,
      },
      {
        path: PAYMENT_LINK_USSD_CODE_DETAILS,
        Component: PaymentLinkUssdCodeDetails,
      },
      {
        path: PAYMENT_LINK_USSD_CODE_PENDING,
        Component: PaymentLinkUssdCodePending,
      },
      {
        path: PAYMENT_LINK_QR_CODE_DETAILS,
        Component: QRCodePaymentLinkDetailsPage,
      },
      {
        path: PAYMENT_LINK_QR_CODE_PENDING,
        Component: QRCodePaymentLinkPendingPage,
      },
      {
        path: PAYMENT_LINK_CARD_PENDING,
        Component: PaymentLinkCardPendingPage,
      },
      {
        path: OPEN_PAYMENT_LINK_METHOD,
        Component: OpenPaymentLinkMethodPage,
      },
      {
        path: OPEN_PAYMENT_LINK_BANK_TRANSFER,
        Component: OpenPaymentLinkBankTransferPage,
      },
      {
        path: OPEN_PAYMENT_LINK_QR_CODE,
        Component: OpenPaymentLinkQrCodePage,
      },
      {
        path: OPEN_PAYMENT_LINK_USSD,
        Component: OpenPaymentLinkUssdPage,
      },
      {
        path: OPEN_PAYMENT_LINK_CARD_PENDING,
        Component: OpenPaymentLinkCardPendingPage,
      },
      {
        path: OPEN_PAYMENT_LINK_SUCCESS,
        Component: OpenPaymentLinkSuccessPage,
      },
    ],
  },
  {
    path: OPEN_PAYMENT_LINK_PAGE,
    Component: OpenPaymentLinkAmountPage,
    errorElement: <RouterChunkErrorBoundary />,
  },
  //#endregion Payment Link flow
  {
    path: SIGN_UP_USER,
    Component: SignUpDetailsLayout,
    errorElement: <RouterChunkErrorBoundary />,
  },
  {
    path: SIGN_UP_PHOTO_VERIFICATION,
    Component: SignUpDetailsLayout,
    errorElement: <RouterChunkErrorBoundary />,
  },
  {
    path: PIN_CREATED,
    Component: PinCreatedPage,
    errorElement: <RouterChunkErrorBoundary />,
  },
  {
    path: ACCOUNT_CREATED,
    Component: AccountCreatedPage,
    errorElement: <RouterChunkErrorBoundary />,
  },
  // * ENDREGION Pages that don't require authentication
  //------------------------------------------------------//
  //------------------------------------------------------//
  // * REGION Pages that require authentication
  {
    Component: TokenHOC,
    errorElement: <RouterChunkErrorBoundary />,
    children: [
      {
        path: UPGRADE_ACCOUNT,
        Component: UpgradeAccountTierPage,
      },
      {
        path: UPGRADE,
        Component: TiersLayout,
      },
      {
        path: END_UPGRADE_PAGE,
        Component: EndUpgradePage,
      },
      {
        Component: MainLayout,
        children: [
          {
            path: DASHBOARD,
            Component: DashboardPage,
          },
          {
            path: COLLECT_PAYMENT,
            Component: CollectPaymentPage,
          },
          {
            path: MAKE_PAYMENT,
            Component: MakePaymentPage,
          },
          // #region Payment Flow
          {
            path: PAYMENT,
            Component: PaymentsPage,
          },
          {
            path: PAYMENT_OVERVIEW,
            Component: PaymentOverviewPage,
          },
          {
            path: PAYMENT_USSD_CODE,
            Component: USSDCodePage,
          },
          {
            path: PAYMENT_USSD_CODE_PENDING,
            Component: USSDCodePendingPage,
          },
          {
            path: PAYMENT_PENDING_SALE,
            Component: PendingSalePage,
          },
          {
            path: PAYMENT_CONFIRM_TRANSFER,
            Component: ConfirmTransferPage,
          },
          {
            path: PAYMENT_QR_CODE,
            Component: QRCodePage,
          },
          {
            path: PAYMENT_QR_CODE_PENDING,
            Component: QRCodePendingPage,
          },
          {
            path: PAYMENT_BANK_TRANSFER,
            Component: BankTransferPage,
          },
          {
            path: PAYMENT_PAYMENT_LINK,
            Component: PaymentLinkPage,
          },
          {
            path: PAYMENT_LINK_CONFIRMATION,
            Component: PaymentLinkConfirmationPage,
          },
          {
            path: PAYMENT_BANK_TRANSFER_PENDING,
            Component: BankTransferPendingPage,
          },
          {
            path: PAYMENT_SUCCESS,
            Component: PaymentSuccessfulPage,
          },
          // #endregion Payment Flow
          // #region Transactions Flow,
          {
            path: TRANSACTIONS,
            Component: TransactionsPage,
          },
          // Refund is hidden for now - any direct/bookmarked hit on a refund
          // URL lands back on the transactions list instead of registering
          // the flow's routes.
          ...(IS_REFUND_ENABLED
            ? [
                {
                  path: TRANSACTIONS_REFUND,
                  Component: RefundPage,
                },
                {
                  path: TRANSACTIONS_REFUND_OVERVIEW,
                  Component: RefundOverviewPage,
                },
                {
                  path: TRANSACTIONS_REFUND_PIN,
                  Component: RefundTransactionPinPage,
                },
                {
                  path: TRANSACTIONS_REFUND_AUTHENTICATION,
                  Component: RefundAuthenticationCodePage,
                },
                {
                  path: TRANSACTIONS_REFUND_SUCCESS,
                  Component: RefundSuccessfulPage,
                },
              ]
            : [
                {
                  path: `${TRANSACTIONS_REFUND}/*`,
                  element: <Navigate to={TRANSACTIONS} replace />,
                },
              ]),
          {
            path: TRANSACTIONS_TRANSACTION_DETAILS,
            Component: TransactionDetailsPage,
          },
          // #endregion Transactions Flow
          // #region Send Money Flow
          {
            path: SEND_MONEY,
            Component: SendMoneyPage,
          },
          {
            path: SEND_MONEY_OVERVIEW,
            Component: SendMoneyOverviewPage,
          },
          {
            path: SEND_MONEY_PIN,
            Component: SendMoneyTransactionPinPage,
          },
          {
            path: SEND_MONEY_AUTHENTICATION,
            Component: SendMoneyAuthenticationCodePage,
          },
          {
            path: SEND_MONEY_SUCCESS,
            Component: SendMoneySuccessfulPage,
          },
          // #endregion Send Money Flow
          // #region Beneficiaries Flow
          {
            path: BENEFICIARIES,
            Component: BeneficiariesPage,
          },
          // #endregion Beneficiaries Flow
          // #region Virtual Accounts Flow
          {
            path: VIRTUAL_ACCOUNTS,
            element: merchantOnly(VirtualAccountsPage, "vaas:manage"),
          },
          {
            path: VIRTUAL_ACCOUNT_CREATE,
            element: merchantOnly(CreateVirtualAccountPage, "vaas:manage"),
          },
          {
            path: VIRTUAL_ACCOUNT_TRANSACTION_DETAILS,
            element: merchantOnly(TransactionDetailsPage, "vaas:manage"),
          },
          {
            path: VIRTUAL_ACCOUNT_DETAILS,
            element: merchantOnly(VirtualAccountDetailsPage, "vaas:manage"),
          },
          // #endregion Virtual Accounts Flow
          // #region Outlet Management Flow
          // * Pilot only — not released to production yet, so the routes are
          // * only registered when the feature is switched on.
          ...(IS_OUTLET_MANAGEMENT_ENABLED
            ? [
                {
                  path: OUTLET_MANAGEMENT,
                  element: merchantOnly(OutletManagementPage, "outlet:read"),
                },
                {
                  path: OUTLET_CREATE,
                  element: merchantOnly(CreateOutletPage, "outlet:create"),
                },
                {
                  path: POS_TRANSACTION_HISTORY,
                  element: merchantOnly(
                    PosTransactionHistoryPage,
                    "outlet:read"
                  ),
                },
                {
                  path: OUTLET_WITHDRAWAL_HISTORY,
                  element: merchantOnly(
                    WithdrawalHistoryPage,
                    "outlet:withdraw"
                  ),
                },
              ]
            : [
                // Any direct/bookmarked hit on an outlet URL lands on the dashboard.
                {
                  path: `${OUTLET_MANAGEMENT}/*`,
                  element: <Navigate to={DASHBOARD} replace />,
                },
              ]),
          // #endregion Outlet Management Flow
          // #region POS Management Flow
          {
            path: POS_MANAGEMENT,
            Component: PosManagementPage,
          },
          {
            path: POS_VIEW_OFFER,
            Component: PosViewOfferPage,
          },
          {
            path: POS_DETAILS,
            Component: PosDetailsPage,
          },
          {
            path: POS_REQUEST_REVIEW,
            Component: PosRequestReviewPage,
          },
          {
            path: POS_REQUEST_SUBMIT,
            Component: PosRequestSubmitPage,
          },
          {
            path: POS_TRANSACTION_PIN,
            Component: PosTransactionPinPage,
          },
          {
            path: OPERATION_CONFIRMATION,
            Component: OperationConfirmationPage,
          },
          {
            path: POS_AUTHENTICATION,
            Component: PosRequestAuthenticationCodePage,
          },
          {
            path: POS_PAYMENT_METHOD,
            Component: PosPaymentMethodPage,
          },
          // #endregion POS Management Flow

          // #region Loans Flow
          // {
          //   path: LOANS,
          //   Component: LoansPage,
          // },
          {
            path: LOANS_SUCCESS,
            Component: LoansSuccessfulPage,
          },
          // #endregion Loans Flow
          // #region Analytics Flow
          {
            path: ANALYTICS,
            Component: AnalyticsPage,
          },
          {
            path: ANALYTICS_UNDO_SALE,
            Component: UndoSalePinPage,
          },
          // #endregion Analytics Flow
          // #region Statements Flow
          {
            path: STATEMENTS,
            Component: StatementsPage,
          },
          // #endregion Statements Flow
          // #region Inventory Flow
          {
            path: INVENTORY,
            Component: InventoryPage,
          },
          { path: ADD_NEW_PRODUCT, Component: AddNewProductPage },
          // #endregion Inventory Flow
          // #region Settings Flow
          {
            path: SETTINGS,
            Component: SettingsPage,
          },
          // #endregion Settings Flow
        ],
      },
      {
        Component: SettingsLayout,
        children: [
          {
            path: PROFILE_MANAGEMENT,
            Component: ProfileManagementPage,
          },
          {
            path: TRANSACTION_PIN,
            Component: TransactionPinPage,
          },
          {
            path: CHANGE_PASSWORD,
            Component: ChangePasswordPage,
          },
          {
            path: TWO_FACTOR_AUTH,
            Component: TwoFactorAuthenticationPage,
          },
          {
            path: TEAM_MANAGEMENT,
            Component: TeamManagementPage,
          },
          {
            path: TEAM_MEMBER_DETAILS,
            Component: TeamMemberDetailsPage,
          },
          {
            path: SYSTEM_PREFERENCE,
            Component: SystemPreferencePage,
          },
          {
            path: NOTIFICATION_PREFERENCES,
            Component: NotificationPreferencesPage,
          },
        ],
      },
    ],
  },
  {
    path: ERROR_HANDLER,
    Component: ErrorHandlerPage,
  },
]);
export default appRoutes;
